"use client"

import Link from "next/link"
import { useState } from "react"

import { ActionBadge } from "@/components/ui/action-badge"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  isCanonicalPackage,
  orderPackages,
  packageBadgeColor,
  packageChipLabel,
  packageChipLabelKey,
  PACKAGE_TO_TEST_SLUG,
  sitePackageIds,
} from "@/lib/categories"
import { localizedPath } from "@/lib/localized-route-slugs"

/**
 * MarkerBreakdown — the deterministic marker-list breakdown rendered in the chat
 * widget alongside Ella's prose (the hybrid design). glue emits this payload from
 * the resolver's projected facts; nothing here is model-generated, so it cannot
 * hallucinate an inclusion status. Compact rows, grouped by status, one primary
 * package chip per row (+ a "+N" to the detail page for multi-package markers),
 * two links per row (marker → biomarker page, chip → test page), long groups
 * expand in place.
 *
 * Design: customer-support docs/superpowers/designs/2026-08-12-biomarker-llm-composition.md §2a.
 */

export type MarkerBucket =
  | "includedBlood"
  | "addon"
  | "imagingOnly"
  | "couldntConfirm"
  | "notOffered"
  | "ambiguous"
  /**
   * A real lab marker we can add to a blood order but do not carry in any
   * package or listed add-on (homocysteine, cystatin C, ...). glue resolves it
   * against the internal lab catalogue and emits this bucket WITHOUT a price:
   * the price is a quote the support team confirms, never a published figure
   * the widget may state (customer-support AHD-11821).
   */
  | "offCatalog"

export interface MarkerRow {
  /** Canonical marker name (or the raw term when unmatched). */
  name: string
  /** Biomarker detail-page slug; omitted for an unmatched term (no page). */
  slug?: string
  bucket: MarkerBucket
  /**
   * Resolver package tags for this marker, e.g. ["blood"] or ["advanced"] or
   * ["addon-hormones-m"]. glue emits these raw facts (the marker's full CMS
   * package set); the widget derives the chip label + test-page link here (see
   * MarkerRowLine) so the product strings + slug map stay in marketing-site.
   */
  packages: string[]
}

export interface MarkerBreakdownData {
  country: string
  locale: string
  markers: MarkerRow[]
}

type GroupKey = "blood" | "addon" | "scan" | "onRequest" | "unresolved"

const BUCKET_GROUP: Record<MarkerBucket, GroupKey> = {
  includedBlood: "blood",
  addon: "addon",
  imagingOnly: "scan",
  offCatalog: "onRequest",
  couldntConfirm: "unresolved",
  notOffered: "unresolved",
  ambiguous: "unresolved",
}

/**
 * Group for a bucket, defaulting to "unresolved" for one this build has never
 * heard of. glue and the widget deploy independently, so a bucket added on the
 * glue side lands here before this file knows it — and a bare
 * `BUCKET_GROUP[bucket]` returns undefined, which matches no group and makes
 * the marker VANISH from the card while Ella's prose still talks about it. A
 * marker in the wrong group is a much smaller lie than a marker that isn't
 * there.
 */
function groupFor(bucket: MarkerBucket): GroupKey {
  return BUCKET_GROUP[bucket] ?? "unresolved"
}

const GROUP: Record<GroupKey, { dot: string; head: string }> = {
  blood: { dot: "bg-deep-green-600", head: "text-deep-green-600" },
  addon: { dot: "bg-sand-600", head: "text-sand-700" },
  scan: { dot: "bg-deep-blue-800", head: "text-deep-blue-800" },
  onRequest: { dot: "bg-aubergine-500", head: "text-aubergine-700" },
  unresolved: { dot: "bg-neutral-400", head: "text-neutral-600" },
}

/** `chat.markerCard` key for each group's heading (localized at render). */
const GROUP_LABEL_KEY: Record<GroupKey, string> = {
  blood: "groupBlood",
  addon: "groupAddon",
  scan: "groupScan",
  onRequest: "groupOnRequest",
  unresolved: "groupUnresolved",
}

// "On request" sits after the three things we sell and before the ones we
// couldn't place: it IS a resolved marker, just not one in a package.
const GROUP_ORDER: GroupKey[] = [
  "blood",
  "addon",
  "scan",
  "onRequest",
  "unresolved",
]
const COLLAPSE_AT = 4
const COLLAPSED_ROWS = 3
// Below this many markers the status grouping is noise — and duplicates the row
// chip — so we render a flat list. The chip colour still encodes status.
const GROUP_THRESHOLD = 10

/**
 * The package a marker's PRIMARY chip leads with, by group — the entry product a
 * customer buys to get this marker. A blood-panel marker carries its MRI tiers
 * too (the MRI packages bundle the blood panel), and `orderPackages` ranks those
 * ahead of `blood`; leading with the tier would point every blood-test marker's
 * chip at the full-body MRI instead of the blood test. Groups not listed keep
 * the canonical highest-tier lead — an imaging-only marker's product IS the MRI.
 */
const GROUP_PRIMARY_PACKAGE: Partial<Record<GroupKey, string>> = {
  blood: "blood",
}

/**
 * The packages a marker belongs to that we render as chips — the marker's full
 * CMS `packages` (glue emits the whole set), minus any unknown tag, in the
 * canonical Core→…→add-on order, with the marker's own group's entry product
 * pulled to the front so the primary chip (index 0) matches the group. This maps
 * *by package id* — unlike the old `deriveProduct`, which only labelled imaging
 * markers carrying an `addon-*` tag and so mislabelled a base-MRI marker like VAT
 * (`packages: ["advanced"]`) as "No match".
 */
function chipPackages(m: MarkerRow): string[] {
  const ordered = orderPackages(
    sitePackageIds(m.packages).filter(isCanonicalPackage)
  )
  const lead = GROUP_PRIMARY_PACKAGE[groupFor(m.bucket)]
  if (lead && ordered.includes(lead)) {
    return [lead, ...ordered.filter((id) => id !== lead)]
  }
  return ordered
}

/** Localized chip label for a package — the `whatWeDetect` string the listing
 *  uses, falling back to the English static label for an unknown id. `t` is a
 *  `whatWeDetect` translator. */
function chipLabel(id: string, t: (key: string) => string): string {
  const key = packageChipLabelKey(id)
  return key ? t(key) : packageChipLabel(id)
}

/**
 * One marker row — name (→ biomarker page) + a single primary product chip
 * (→ test page), and a "+N" (→ biomarker page, where the full membership
 * shows) when the marker is in more than one package. A marker we couldn't map
 * to any package reads "No match" — except an `offCatalog` one, which we DID
 * recognise and can add individually, so it reads "On request" instead.
 * Chip labels are localized via `t` (the same `whatWeDetect` strings the
 * listing uses).
 */
function MarkerRowLine({
  m,
  base,
  markerBase,
  t,
  noMatchLabel,
  onRequestLabel,
}: {
  m: MarkerRow
  base: string
  /** `base` + the biomarker section segment, already translated for the locale. */
  markerBase: string
  t: (key: string) => string
  noMatchLabel: string
  onRequestLabel: string
}) {
  const markerHref = m.slug ? `${markerBase}/${m.slug}` : undefined
  const fallbackLabel =
    m.bucket === "offCatalog" ? onRequestLabel : noMatchLabel
  const pkgs = chipPackages(m)
  const primary = pkgs[0]
  const extra = pkgs.length - 1
  const testSlug = primary ? PACKAGE_TO_TEST_SLUG[primary] : undefined
  return (
    <div className="type-body-md flex items-center gap-2.5 border-t border-neutral-100 px-4 py-2 first:border-t-0">
      {markerHref ? (
        <Link
          href={markerHref}
          className="text-foreground hover:text-deep-blue-800 min-w-0 truncate hover:underline"
        >
          {m.name}
        </Link>
      ) : (
        <span className="text-foreground min-w-0 truncate">{m.name}</span>
      )}
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        {primary ? (
          testSlug ? (
            <Link
              href={`${base}/tests/${testSlug}`}
              className="hover:underline"
            >
              <ActionBadge
                label={chipLabel(primary, t)}
                color={packageBadgeColor(primary)}
              />
            </Link>
          ) : (
            <ActionBadge
              label={chipLabel(primary, t)}
              color={packageBadgeColor(primary)}
            />
          )
        ) : (
          <ActionBadge label={fallbackLabel} color="neutral" />
        )}
        {extra > 0 && markerHref && (
          <Link
            href={markerHref}
            className="type-body-sm hover:text-deep-blue-800 shrink-0 text-neutral-400 hover:underline"
          >
            +{extra}
          </Link>
        )}
      </span>
    </div>
  )
}

export function MarkerBreakdown({ data }: { data: MarkerBreakdownData }) {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  // Chip labels read from the same `whatWeDetect` strings as the biomarkers
  // listing (localized "Bluttest", "Hormone (Frauen)"…); the card's own chrome
  // (heading, group titles, "No match", footer) lives under `chat.markerCard`.
  const t = useTranslations("whatWeDetect")
  const mt = useTranslations("chat.markerCard")
  // next-intl types t() to its known keys; our package keys are resolved
  // dynamically (the listing casts these the same way — `wt(k as never)`), so
  // widen once for the chip helper rather than casting at every call.
  const tChip = t as unknown as (key: string) => string
  // Same widening for the chrome translator — one call takes a dynamic key
  // (group heading) and one takes an ICU {count}.
  const mtc = mt as unknown as (
    key: string,
    values?: Record<string, unknown>
  ) => string
  const noMatchLabel = mt("noMatch")
  const onRequestLabel = mt("onRequest")
  const base = `/${data.country}/${data.locale}`
  // The biomarker section segment is translated per locale (FR: /biomarqueurs) —
  // link straight there rather than through the 308.
  const markerBase = `${base}${localizedPath("/biomarkers", data.locale)}`
  const groups = GROUP_ORDER.map((g) => ({
    g,
    rows: data.markers.filter((m) => groupFor(m.bucket) === g),
  })).filter((x) => x.rows.length > 0)
  const grouped = data.markers.length >= GROUP_THRESHOLD

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-4 py-2.5">
        <span className="type-body-sm font-medium text-neutral-900">
          {mt("title")}
        </span>
      </div>

      {grouped
        ? groups.map(({ g, rows }) => {
            const cfg = GROUP[g]
            const collapsible = rows.length > COLLAPSE_AT
            const isOpen = Boolean(open[g])
            const shown =
              collapsible && !isOpen ? rows.slice(0, COLLAPSED_ROWS) : rows
            return (
              <div key={g}>
                <div
                  className={`type-label-sm flex items-center gap-2 px-4 pt-2.5 pb-1.5 font-medium uppercase ${cfg.head}`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${cfg.dot}`}
                    aria-hidden
                  />
                  {mtc(GROUP_LABEL_KEY[g])}
                  <span className="font-normal tracking-normal text-neutral-300 normal-case">
                    {rows.length}
                  </span>
                </div>

                {shown.map((m) => (
                  <MarkerRowLine
                    key={m.name}
                    m={m}
                    base={base}
                    markerBase={markerBase}
                    t={tChip}
                    noMatchLabel={noMatchLabel}
                    onRequestLabel={onRequestLabel}
                  />
                ))}

                {collapsible && (
                  <button
                    type="button"
                    onClick={() => setOpen((s) => ({ ...s, [g]: !s[g] }))}
                    className="type-body-sm text-deep-blue-800 block w-full border-t border-neutral-100 px-4 py-2 text-left font-medium hover:bg-neutral-50"
                  >
                    {isOpen
                      ? mt("showLess")
                      : mtc("showMore", {
                          count: rows.length - COLLAPSED_ROWS,
                        })}
                  </button>
                )}
              </div>
            )
          })
        : // Short list: no group headers — rows stay ordered by status and the
          // chip colour carries the status on its own.
          groups
            .flatMap(({ rows }) => rows)
            .map((m) => (
              <MarkerRowLine
                key={m.name}
                m={m}
                base={base}
                markerBase={markerBase}
                t={tChip}
                noMatchLabel={noMatchLabel}
                onRequestLabel={onRequestLabel}
              />
            ))}

      <Link
        href={`${base}/tests`}
        className="type-body-sm text-deep-blue-800 block border-t border-neutral-100 px-4 py-3 font-medium hover:bg-neutral-50"
      >
        {mt("seeAllTests")} →
      </Link>
    </div>
  )
}
